import React, { useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import { toast } from 'react-toastify'
import { selectComplianceEnabled } from 'features/auth/ducks/selectors'
import LayersRoundedIcon from '@mui/icons-material/LayersRounded'
import HomeWorkRoundedIcon from '@mui/icons-material/HomeWorkRounded'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import { font, ink, line, radius, shadow, surface, transition } from 'features/dashboard/tokens'
import { scoreColour, formatPercent, scoreBand, RAG } from 'features/dashboard/charts/scoreColour'
import { formatDate } from 'utils/date'
import { createEstate, deleteEstate } from 'services/estateService'
import AddToPortfolioMenu from 'features/portfolios/components/AddToPortfolioMenu'
import SearchableSelect from 'components/SearchableSelect'

const NOT_SET = '__not_set__'

const ESTATE_ACCENT = '#7C5FE0'

const STATUS_LEVEL = { active: 'low', inactive: 'critical', dormant: 'medium' }

const getStatusColour = status => {
  const level = STATUS_LEVEL[status?.toLowerCase()]
  if (!level) return { bg: RAG.none.bg, text: RAG.none.fg }
  return { bg: RAG[level].bg, text: RAG[level].fg }
}

const scoreTitle = score =>
  score === null || score === undefined
    ? 'No compliance score yet — the share of weighted obligations satisfied could not be calculated.'
    : `Compliance score ${formatPercent(score)} (${scoreBand(score)}) — the share of weighted obligations satisfied, where each obligation is weighted by its criticality.`

const warnTitle = count =>
  `${count} unmet statutory obligation${count === 1 ? '' : 's'} — legally required by legislation, with no evidence on file.`

const ScorePill = ({ score }) => (
  <Pill $colour={scoreColour(score)} title={scoreTitle(score)} aria-label={scoreTitle(score)}>
    {formatPercent(score)}
  </Pill>
)

const StatutoryRisk = ({ count }) =>
  count > 0 ? (
    <Risk title={warnTitle(count)} aria-label={warnTitle(count)}>
      <RiskGlyph aria-hidden='true'>⚠</RiskGlyph>
      {count}
    </Risk>
  ) : null

const PortfolioContents = ({ portfolioId, estates = [], buildings = [], onOpenBp, onDrillEstate, onCreated }) => {
  const complianceEnabled = useSelector(selectComplianceEnabled)
  const focusedPortfolioId = portfolioId && portfolioId !== 'ungrouped' ? portfolioId : ''
  const [selecting, setSelecting] = useState(false)
  const [selected, setSelected] = useState(() => new Set())
  const [naming, setNaming] = useState(false)
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const [deletingId, setDeletingId] = useState(null)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [ownerFilter, setOwnerFilter] = useState('all')
  const [cityFilter, setCityFilter] = useState('all')
  const [sort, setSort] = useState({ key: null, dir: 'asc' })

  const sortBy = key => setSort(s => s.key === key
    ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
    : { key, dir: 'asc' })

  const statusOptions = useMemo(() => Array.from(new Set(buildings.map(b => b.status).filter(Boolean))).sort(), [buildings])
  const ownerOptions = useMemo(() => Array.from(new Set(buildings.map(b => b.buildingOwner).filter(Boolean))).sort(), [buildings])
  const hasUnsetOwner = useMemo(() => buildings.some(b => !b.buildingOwner), [buildings])
  const cityOptions = useMemo(() => Array.from(new Set(buildings.map(b => b.city).filter(Boolean))).sort(), [buildings])

  const filteredBuildings = useMemo(() => {
    const q = search.trim().toLowerCase()
    return buildings.filter(b => {
      if (statusFilter !== 'all' && (b.status || '') !== statusFilter) return false
      if (ownerFilter === NOT_SET && b.buildingOwner) return false
      if (ownerFilter !== 'all' && ownerFilter !== NOT_SET && (b.buildingOwner || '') !== ownerFilter) return false
      if (cityFilter !== 'all' && (b.city || '') !== cityFilter) return false
      if (!q) return true
      return [b.name, b.uprn, b.city, b.postalCode, b.buildingOwner]
        .filter(Boolean).join(' ').toLowerCase().includes(q)
    })
  }, [buildings, search, statusFilter, ownerFilter, cityFilter])

  const sortRows = useCallback(rows => {
    if (!sort.key) return rows
    const dir = sort.dir === 'asc' ? 1 : -1
    const val = b => {
      switch (sort.key) {
        case 'name': return (b.name || '').toLowerCase()
        case 'uprn': return (b.uprn || '').toLowerCase()
        case 'city': return (b.city || '').toLowerCase()
        case 'postalCode': return (b.postalCode || '').toLowerCase()
        case 'buildingOwner': return b.buildingOwner ? b.buildingOwner.toLowerCase() : '\uffff'
        case 'status': return (b.status || '').toLowerCase()
        case 'createdAt': return b.createdAt ? new Date(b.createdAt).getTime() : 0
        case 'score': return b.score ?? -1
        default: return 0
      }
    }
    return [...rows].sort((a, b) => {
      const av = val(a), bv = val(b)
      return av < bv ? -1 * dir : av > bv ? 1 * dir : 0
    })
  }, [sort])

  const byEstate = useMemo(() => {
    const map = new Map()
    filteredBuildings.forEach(b => {
      if (!b.estateId) return
      if (!map.has(b.estateId)) map.set(b.estateId, [])
      map.get(b.estateId).push(b)
    })
    map.forEach((rows, key) => map.set(key, sortRows(rows)))
    return map
  }, [filteredBuildings, sortRows])
  const ungrouped = useMemo(
    () => sortRows(filteredBuildings.filter(b => !b.estateId)),
    [filteredBuildings, sortRows]
  )

  const toggleSelect = id => setSelected(prev => {
    const next = new Set(prev)
    next.has(id) ? next.delete(id) : next.add(id)
    return next
  })

  const resetSelection = () => {
    setSelecting(false)
    setNaming(false)
    setName('')
    setSelected(new Set())
  }

  const startNaming = () => {
    setNaming(true)
  }

  const createFromSelection = async () => {
    if (!name.trim() || selected.size === 0 || busy || !focusedPortfolioId) return
    setBusy(true)
    try {
      await createEstate({
        name: name.trim(),
        buildingPassportIds: Array.from(selected),
        portfolioId: focusedPortfolioId,
      })
      toast.success('Estate created in this portfolio.')
      resetSelection()
      onCreated && onCreated()
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Could not create the estate.')
    } finally {
      setBusy(false)
    }
  }

  const handleDelete = async estate => {
    if (deletingId) return
    if (!window.confirm(`Delete the estate “${estate.name}”? Its Building Passports are kept and moved back to ungrouped.`)) return
    setDeletingId(estate.id)
    try {
      await deleteEstate(estate.id)
      toast.success('Estate deleted.')
      onCreated && onCreated()
    } catch (e) {
      toast.error('Could not delete the estate.')
    } finally {
      setDeletingId(null)
    }
  }

  if (estates.length === 0 && buildings.length === 0) {
    return <Empty>This portfolio is empty — add Building Passports to it, or group some into an estate.</Empty>
  }

  const colCount = 8 + (complianceEnabled ? 1 : 0) + (selecting ? 1 : 0)

  const renderBuildingRow = (bp, selectable) => {
    const statusColours = getStatusColour(bp.status)
    const isChecked = selected.has(bp.id)
    const handleRowClick = selecting && selectable ? () => toggleSelect(bp.id) : () => onOpenBp(bp.id)
    return (
      <tr
        key={bp.id}
        onClick={handleRowClick}
        title={selecting && selectable ? `Select ${bp.name}` : `Open ${bp.name}`}
        className={selecting && selectable && isChecked ? 'is-checked' : undefined}
      >
        {selecting && (
          <td onClick={e => e.stopPropagation()}>
            {selectable && (
              <Check
                type='checkbox'
                checked={isChecked}
                onChange={() => toggleSelect(bp.id)}
                aria-label={`Select ${bp.name} to group into an estate`}
              />
            )}
          </td>
        )}
        <td><CellName>{bp.name}</CellName></td>
        <td>{bp.uprn || '-'}</td>
        <td>{bp.city || '-'}</td>
        <td>{bp.postalCode || '-'}</td>
        <td>{bp.buildingOwner || <NotSet title='No building owner recorded yet. Add it from the building page.'>Not set</NotSet>}</td>
        <td>
          {bp.status && (
            <StatusBadge $bg={statusColours.bg} $text={statusColours.text}>{bp.status}</StatusBadge>
          )}
        </td>
        <td>{formatDate(bp.createdAt)}</td>
        {complianceEnabled && <td><ScorePill score={bp.score} /></td>}
        <td style={{ textAlign: 'right' }}>
          {!selecting && (
            <RowActions>
              <AddToPortfolioMenu
                bpId={bp.id}
                bpName={bp.name}
                currentPortfolioId={focusedPortfolioId || null}
                onAssigned={onCreated}
              />
              <ArrowIcon>→</ArrowIcon>
            </RowActions>
          )}
        </td>
      </tr>
    )
  }

  const header = (label, key) => (
    <SortTh $active={sort.key === key} onClick={() => sortBy(key)}>
      {label}{sort.key === key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
    </SortTh>
  )

  return (
    <Wrap>
      {buildings.length > 0 && (
        <Controls>
          <SearchInput
            placeholder='Search name, UPRN, city, post code or building owner…'
            value={search}
            onChange={e => setSearch(e.target.value)}
            aria-label='Search Building Passports'
          />
          {statusOptions.length > 1 && (
            <FilterField>
              <SearchableSelect
                value={statusFilter}
                onChange={v => setStatusFilter(v)}
                options={[{ value: 'all', label: 'All statuses' }, ...statusOptions.map(s => ({ value: s, label: s, color: getStatusColour(s).text }))]}
                ariaLabel='Filter by status'
              />
            </FilterField>
          )}
          {(ownerOptions.length > 1 || (ownerOptions.length > 0 && hasUnsetOwner)) && (
            <FilterField>
              <SearchableSelect
                value={ownerFilter}
                onChange={v => setOwnerFilter(v)}
                options={[{ value: 'all', label: 'All building owners' }, ...ownerOptions.map(c => ({ value: c, label: c })), ...(hasUnsetOwner ? [{ value: NOT_SET, label: 'Not set' }] : [])]}
                searchPlaceholder='Search companies…'
                ariaLabel='Filter by building owner'
              />
            </FilterField>
          )}
          {cityOptions.length > 1 && (
            <FilterField>
              <SearchableSelect
                value={cityFilter}
                onChange={v => setCityFilter(v)}
                options={[{ value: 'all', label: 'All cities' }, ...cityOptions.map(c => ({ value: c, label: c }))]}
                searchPlaceholder='Search cities…'
                ariaLabel='Filter by city'
              />
            </FilterField>
          )}
          <Count>Showing {filteredBuildings.length} of {buildings.length} Building Passports</Count>
        </Controls>
      )}
      <TableContainer>
        <StyledTable>
          <thead>
            <tr>
              {selecting && <th style={{ width: '44px' }} />}
              {header('Name', 'name')}
              {header('UPRN', 'uprn')}
              {header('City', 'city')}
              {header('Post Code', 'postalCode')}
              {header('Building owner', 'buildingOwner')}
              {header('Status', 'status')}
              {header('Created', 'createdAt')}
              {complianceEnabled && header('Compliance', 'score')}
              <th />
            </tr>
          </thead>
          <tbody>
            {estates.map(estate => {
              const estBps = byEstate.get(estate.id) || []
              return (
                <React.Fragment key={estate.id}>
                  <EstateRow onClick={() => onDrillEstate(estate.id)} title={`Open ${estate.name}`}>
                    <td colSpan={colCount}>
                      <EstateHead>
                        <EstateBadge aria-hidden='true'><LayersRoundedIcon style={{ fontSize: 16 }} /></EstateBadge>
                        <EstateName>{estate.name}</EstateName>
                        <EstateMeta>{estBps.length} building{estBps.length === 1 ? '' : 's'}</EstateMeta>
                        {complianceEnabled && <ScorePill score={estate.score} />}
                        {complianceEnabled && <StatutoryRisk count={estate.unmetStatutory} />}
                        <EstateSpacer />
                        <EstateActions onClick={e => e.stopPropagation()}>
                          <DeleteBtn
                            type='button'
                            title='Delete estate'
                            aria-label={`Delete ${estate.name}`}
                            disabled={deletingId === estate.id}
                            onClick={() => handleDelete(estate)}
                          >
                            <DeleteOutlineRoundedIcon style={{ fontSize: 17 }} />
                          </DeleteBtn>
                          <OpenLink type='button' onClick={() => onDrillEstate(estate.id)}>Open <span aria-hidden='true'>→</span></OpenLink>
                        </EstateActions>
                      </EstateHead>
                    </td>
                  </EstateRow>
                  {estBps.length === 0 ? (
                    <tr><EmptyCell colSpan={colCount}>No Building Passports in this estate yet.</EmptyCell></tr>
                  ) : estBps.map(bp => renderBuildingRow(bp, false))}
                </React.Fragment>
              )
            })}

            {ungrouped.length > 0 && (
              <>
                <EstateRow $ungrouped as='tr'>
                  <td colSpan={colCount}>
                    <EstateHead>
                      <EstateBadge $ungrouped aria-hidden='true'><HomeWorkRoundedIcon style={{ fontSize: 16 }} /></EstateBadge>
                      <EstateName $ungrouped>{estates.length > 0 ? 'Not in an estate' : 'Ungrouped buildings'}</EstateName>
                      <EstateMeta>{ungrouped.length} building{ungrouped.length === 1 ? '' : 's'}</EstateMeta>
                      <EstateSpacer />
                      <EstateActions onClick={e => e.stopPropagation()}>
                        {focusedPortfolioId && (selecting ? (
                          <EstateMeta>{selected.size} selected</EstateMeta>
                        ) : (
                          <SecondaryBtn type='button' onClick={() => setSelecting(true)}>
                            <LayersRoundedIcon style={{ fontSize: 15 }} />
                            Group into a new estate
                          </SecondaryBtn>
                        ))}
                      </EstateActions>
                    </EstateHead>
                  </td>
                </EstateRow>
                {ungrouped.map(bp => renderBuildingRow(bp, true))}
              </>
            )}
          </tbody>
        </StyledTable>
      </TableContainer>

      {selecting && (
        <CreateBar>
          {!naming ? (
            <>
              <SelectHint>Tick the ungrouped Building Passports above, then group them into a new estate.</SelectHint>
              <BarActions>
                <CreateBtn type='button' $primary disabled={selected.size === 0} onClick={startNaming}>
                  Group {selected.size || ''} into a new estate
                </CreateBtn>
                <CancelBtn type='button' onClick={resetSelection}>Cancel</CancelBtn>
              </BarActions>
            </>
          ) : (
            <NameRow>
              <NameInput
                autoFocus
                placeholder='Estate name…'
                value={name}
                onChange={e => setName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') createFromSelection() }}
              />
              <CreateBtn type='button' $primary disabled={busy || !name.trim()} onClick={createFromSelection}>
                {busy ? 'Creating…' : `Create estate · ${selected.size}`}
              </CreateBtn>
              <CancelBtn type='button' onClick={() => { setNaming(false); setName('') }}>Back</CancelBtn>
            </NameRow>
          )}
        </CreateBar>
      )}
    </Wrap>
  )
}

export default PortfolioContents

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`

const TableContainer = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.03);
  overflow: hidden;
  width: 100%;

  @media (max-width: 768px) {
    overflow-x: auto;
  }
`

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`

const SearchInput = styled.input`
  flex: 1;
  min-width: 220px;
  padding: 8px 12px;
  font: inherit;
  font-size: ${font.body};
  border: 1px solid ${line.strong};
  border-radius: ${radius.pill};
  color: ${ink.base};
  background: ${surface.card};
  outline: none;
  &::placeholder { color: ${ink.mute}; }
  &:focus { border-color: ${({ theme }) => theme.colors.primary}; }
`

const FilterField = styled.div`
  min-width: 160px;
`

const Count = styled.span`
  margin-left: auto;
  font-size: ${font.small};
  color: ${ink.mute};
  font-variant-numeric: tabular-nums;
`

const SortTh = styled.th`
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
  color: ${({ $active }) => ($active ? ink.base : ink.mute)} !important;

  &:hover { color: ${ink.base} !important; }
`

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  text-align: left;

  thead {
    background-color: #f9fafb;
    border-bottom: 1px solid #e5e7eb;

    th {
      padding: 14px 24px;
      font-size: 12px;
      font-weight: 600;
      text-transform: uppercase;
      color: #6b7280;
      letter-spacing: 0.05em;
    }
  }

  tbody {
    tr {
      border-bottom: 1px solid #f3f4f6;
      transition: all 0.2s;
      cursor: pointer;

      &:last-child { border-bottom: none; }

      &:hover {
        background-color: #f9fafb;
        td:first-child span { color: ${({ theme }) => theme.colors.primary}; }
      }

      &.is-checked { background-color: ${ESTATE_ACCENT}0F; }

      td {
        padding: 13px 24px;
        font-size: 14px;
        color: #111827;
        vertical-align: middle;
      }
    }
  }
`

const EstateRow = styled.tr`
  cursor: ${({ $ungrouped }) => ($ungrouped ? 'default' : 'pointer')} !important;
  background: ${({ $ungrouped }) => ($ungrouped ? '#f7f8fa' : `${ESTATE_ACCENT}0c`)} !important;
  border-top: 1px solid ${line.strong};

  &:hover {
    background: ${({ $ungrouped }) => ($ungrouped ? '#f7f8fa' : `${ESTATE_ACCENT}16`)} !important;
    td:first-child span { color: inherit !important; }
  }

  & > td { padding: 0 !important; }
`

const EstateHead = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 20px;
  flex-wrap: wrap;
`

const EstateBadge = styled.span`
  flex-shrink: 0;
  display: inline-flex; align-items: center; justify-content: center;
  width: 28px; height: 28px;
  border-radius: ${radius.sm};
  background: ${({ $ungrouped }) => ($ungrouped ? '#eceef1' : `${ESTATE_ACCENT}1f`)};
  color: ${({ $ungrouped }) => ($ungrouped ? ink.mute : ESTATE_ACCENT)};
`

const EstateName = styled.span`
  font-size: ${font.body};
  font-weight: 800;
  color: ${({ $ungrouped }) => ($ungrouped ? ink.soft : ink.base)};
`

const EstateMeta = styled.span`
  font-size: ${font.caption};
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${ink.mute};
`

const EstateSpacer = styled.span`
  flex: 1;
`

const EstateActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
`

const OpenLink = styled.button`
  font: inherit;
  display: inline-flex; align-items: center; gap: 4px;
  padding: 4px 12px;
  border: 1px solid ${ESTATE_ACCENT}55;
  border-radius: ${radius.pill};
  background: ${ESTATE_ACCENT}0f;
  color: ${ESTATE_ACCENT};
  font-size: ${font.caption};
  font-weight: 700;
  cursor: pointer;
  transition: ${transition};
  &:hover { background: ${ESTATE_ACCENT}; color: #fff; }
`

const EmptyCell = styled.td`
  padding: 12px 24px 12px 58px !important;
  font-size: ${font.small} !important;
  color: ${ink.mute} !important;
  font-style: italic;
`

const CellName = styled.span`
  font-weight: 600;
  color: #110c2f;
  transition: color 0.2s;
`

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 9999px;
  font-size: 12px;
  font-weight: 600;
  background-color: ${props => props.$bg || '#f3f4f6'};
  color: ${props => props.$text || '#374151'};
`

const ArrowIcon = styled.span`
  color: ${({ theme }) => theme.colors.primary};
  font-weight: bold;
  font-size: 18px;
`

const RowActions = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
`

const DeleteBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 1px solid ${line.base};
  border-radius: ${radius.sm};
  background: ${surface.card};
  color: ${ink.mute};
  cursor: pointer;
  transition: ${transition};

  &:hover:not(:disabled) {
    border-color: #b00020;
    color: #b00020;
    background: #fdecea;
  }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`

const Pill = styled.span`
  display: inline-flex;
  padding: 2px 10px;
  border-radius: ${radius.pill};
  background: ${({ $colour }) => `${$colour}14`};
  border: 1px solid ${({ $colour }) => `${$colour}40`};
  color: ${({ $colour }) => $colour};
  font-size: ${font.caption};
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  cursor: help;
`

const Risk = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 8px;
  border-radius: ${radius.pill};
  background: ${RAG.critical.bg};
  border: 1px solid ${RAG.critical.fg}33;
  font-size: ${font.caption};
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  color: ${RAG.critical.fg};
  cursor: help;
`

const RiskGlyph = styled.span`
  font-size: 11px;
  line-height: 1;
`

const Check = styled.input`
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: ${ESTATE_ACCENT};
`

const CreateBar = styled.div`
  padding: 12px 16px;
  border: 1px solid ${line.base};
  border-radius: ${radius.md};
  background: ${surface.wash};
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const SelectHint = styled.div`
  font-size: ${font.small};
  color: ${ink.mute};
`

const BarActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`

const SecondaryBtn = styled.button`
  font: inherit;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 13px;
  border: 1px solid ${line.strong};
  border-radius: ${radius.pill};
  background: ${surface.card};
  color: ${ink.soft};
  font-size: ${font.caption};
  font-weight: 700;
  letter-spacing: 0.02em;
  cursor: pointer;
  transition: ${transition};
  &:hover { border-color: ${ESTATE_ACCENT}; color: ${ESTATE_ACCENT}; }
`

const CreateBtn = styled.button`
  font: inherit;
  padding: 7px 14px;
  border: 1px solid ${({ $primary, theme }) => ($primary ? theme.colors.primary : line.strong)};
  border-radius: ${radius.pill};
  background: ${({ $primary, theme }) => ($primary ? theme.colors.primary : surface.card)};
  color: ${({ $primary }) => ($primary ? '#fff' : ink.base)};
  font-size: ${font.caption};
  font-weight: 700;
  cursor: pointer;
  transition: ${transition};
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  &:hover:not(:disabled) { border-color: ${({ theme }) => theme.colors.primary}; }
`

const NameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`

const NameInput = styled.input`
  flex: 1;
  min-width: 160px;
  padding: 7px 12px;
  border: 1px solid ${line.strong};
  border-radius: ${radius.sm};
  font-size: ${font.body};
  outline: none;
  &:focus { border-color: ${({ theme }) => theme.colors.primary}; }
`

const CancelBtn = styled.button`
  font: inherit;
  border: none;
  background: transparent;
  color: ${ink.mute};
  font-size: ${font.caption};
  font-weight: 700;
  cursor: pointer;
  &:hover { color: ${ink.base}; }
`

const Empty = styled.div`
  background: ${surface.card};
  border: 1px solid ${line.base};
  border-radius: ${radius.md};
  box-shadow: ${shadow.card};
  padding: 32px;
  text-align: center;
  font-size: ${font.body};
  color: ${ink.mute};
`

const NotSet = styled.span`
  color: #6b7280;
`
